import { Conditional } from '@payaca/ambrosia';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {
  NavLink,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';

export type TabPanel = {
  icon?: TIconName;
  label: string;
  slug: string;
  render: () => ReactNode;
};

export interface RouterTabsProps {
  panels: TabPanel[];
}

const RouterTabsContext = createContext<RouterTabsProps>({ panels: [] });

const RouterTabs: FC<PropsWithChildren<RouterTabsProps>> = (props) => {
  const { panels, children } = props;

  return (
    <RouterTabsContext.Provider value={{ panels }}>
      {children}
    </RouterTabsContext.Provider>
  );
};

const RouterTabNav: FC<{
  defaultTabSlug?: string;
  className?: string;
  fullWidth?: boolean;
  navSuffix?: ReactNode;
  onChange?: (tabSlug: string) => void;
}> = (props) => {
  const {
    className,
    defaultTabSlug,
    navSuffix,
    onChange,
    fullWidth = false,
  } = props;

  const { panels } = useContext(RouterTabsContext);

  const history = useHistory();
  const { path, url } = useRouteMatch();
  const hasActiveTab = useRouteMatch<{
    tabSlug: string;
  }>(`${path}/:tabSlug`);
  const queryString = new URLSearchParams(window.location.search).toString();
  const navRef = useRef<HTMLElement>(null);

  const activeTabIsValid =
    Boolean(hasActiveTab) &&
    panels.some((panel) => panel.slug === hasActiveTab?.params.tabSlug);

  useEffect(() => {
    if (panels.length === 0) return;
    if (defaultTabSlug && !activeTabIsValid) {
      history.replace(
        `${url}/${defaultTabSlug}${queryString ? `?${queryString}` : ''}`
      );
    }
  }, [
    history,
    activeTabIsValid,
    url,
    queryString,
    defaultTabSlug,
    panels.length,
  ]);

  useEffect(() => {
    if (hasActiveTab?.params?.tabSlug) {
      onChange?.(hasActiveTab.params.tabSlug);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'onChange', and 'hasActiveTab.params.tabSlug'
  }, [hasActiveTab?.url]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]');
    if (!active) return;
    const isFullyVisible =
      active.offsetLeft >= nav.scrollLeft &&
      active.offsetLeft + active.clientWidth <=
        nav.scrollLeft + nav.clientWidth;
    if (isFullyVisible) return;
    nav.scrollLeft =
      active.offsetLeft - nav.clientWidth / 2 + active.clientWidth / 2;
  }, [hasActiveTab?.params?.tabSlug, panels.length]);

  return (
    <div
      className={clstx(
        'grid justify-between',
        navSuffix && 'gap-x-4',
        className
      )}
      style={{
        gridTemplateColumns: navSuffix ? '1fr auto' : '1fr',
      }}
    >
      <nav
        ref={navRef}
        className="typography:prose horizontal-scroll-shadows flex w-full items-end gap-x-4 overflow-x-auto"
        aria-label="Tabs"
        role="tablist"
      >
        {panels.map((tab) => (
          <NavLink
            key={tab.label}
            className={
              'inline-flex cursor-pointer items-center justify-center gap-x-3 whitespace-nowrap border-b-2 border-transparent bg-transparent px-2 py-2.5 text-blue-900 hover:no-underline disabled:pointer-events-none disabled:opacity-50' +
              (fullWidth ? ' flex-[1_1_0px]' : '')
            }
            activeClassName="!border-blue-600 font-semibold"
            to={`${url}/${tab.slug}`}
            role="tab"
          >
            <Conditional condition={!!tab.icon}>
              <UntitledIcon name={tab.icon!} className="h-5 w-5" />
            </Conditional>

            {tab.label}
          </NavLink>
        ))}
      </nav>
      {navSuffix && navSuffix}
    </div>
  );
};

const RouterTabPanels: FC = () => {
  const { panels } = useContext(RouterTabsContext);

  const { path } = useRouteMatch();

  return (
    <Switch>
      {panels.map((tab) => {
        return (
          <Route key={tab.label} path={`${path}/${tab.slug}`}>
            {tab.render()}
          </Route>
        );
      })}
    </Switch>
  );
};

export default Object.assign(RouterTabs, {
  Nav: RouterTabNav,
  Panels: RouterTabPanels,
});
